<template>
  <div>
    <v-toolbar color="grey lighten-3">
      <v-toolbar-title>{{ $t('Add {0}', [$t('Inventory Count')]) }}</v-toolbar-title>
    </v-toolbar>
    <v-card tile>
      <v-container
        fluid
        grid-list-lg
      >
        <v-card-text class="pl-0 pr-0">
          <v-alert
            class="mb-6"
            color="primary"
            icon="fal fa-info-square"
            outlined
            prominent
            text
          >{{ $t('Schedule a full or partial inventory count to maintain accurate inventory levels.') }}
          </v-alert>
          <form-wrapper :validator="$v.inventoryCount">
            <v-form
              ref="form"
              @submit.prevent
            >
              <v-row wrap>
                <v-col
                  class="pb-0"
                  sm="6"
                >
                  <form-group name="name">
                    <template v-slot:default="{attrs}">
                      <v-text-field
                        ref="input"
                        :label="$t('Description')"
                        v-bind="attrs"
                        v-model="inventoryCount.name"
                        filled
                        prepend-inner-icon="fal fa-marker mr-2"
                      />
                    </template>
                  </form-group>
                </v-col>
                <v-col
                  sm="6"
                  class="pb-0"
                >
                  <v-text-field
                    v-if="cashRegister.store"
                    v-model="cashRegister.store.name"
                    :label="$t('Store')"
                    autocomplete="off"
                    filled
                    prepend-inner-icon="fal fa-store-alt mr-2"
                    readonly
                    disabled
                  />
                </v-col>

                <v-col
                  class="pt-0 pb-0"
                  sm="12"
                >
                  <v-row wrap>
                    <v-col sm="4">
                      <v-card
                        :ripple="{ class: 'primary--text', center: true }"
                        class="mt-4 elevation-0"
                        color="grey lighten-4"
                        @click="$set(inventoryCount, 'type', INVENTORY_COUNT_PARTIAL);"
                      >
                        <v-card-title
                          :class="{ 'v-card--active': inventoryCount.type === INVENTORY_COUNT_PARTIAL }"
                          class="pt-4"
                          primary-title
                        >
                          <div class="text-h6 mb-2">{{ $t('Partial {0}', [$t('Inventory Count').toLowerCase()]) }}</div>
                          <span class="text-subtitle-2">{{ $t('Specify the products to include in this inventory count yourself.') }}</span>
                        </v-card-title>
                      </v-card>
                    </v-col>
                    <v-col sm="4">
                      <v-card
                        :ripple="{ class: 'primary--text', center: true }"
                        class="mt-4 elevation-0"
                        color="grey lighten-4"
                        @click="$set(inventoryCount, 'type', INVENTORY_COUNT_FULL)"
                      >
                        <v-card-title
                          :class="{ 'v-card--active': inventoryCount.type === INVENTORY_COUNT_FULL }"
                          class="pt-4"
                          primary-title
                        >
                          <div class="text-h6 mb-2">{{ $t('Full {0}', [$t('Inventory Count').toLowerCase()]) }}</div>
                          <span class="text-subtitle-2">{{ $t('Include all products in this inventory count.') }}</span>
                        </v-card-title>
                      </v-card>
                    </v-col>
                    <v-col sm="4">
                      <v-card
                        :ripple="{ class: 'primary--text', center: true }"
                        class="mt-4 elevation-0"
                        color="grey lighten-4"
                        @click="$set(inventoryCount, 'type', INVENTORY_COUNT_INCOMING)"
                      >
                        <v-card-title
                          :class="{ 'v-card--active': inventoryCount.type === INVENTORY_COUNT_INCOMING }"
                          class="pt-4"
                          primary-title
                        >
                          <div class="text-h6 mb-2">{{ $t('Incoming Order') }}</div>
                          <span class="text-subtitle-2">{{ $t('Add received goods to the inventory stock.') }}</span>
                        </v-card-title>
                      </v-card>
                    </v-col>
                    <v-col
                      class="pb-0 pt-0"
                      offset-sm="4"
                      sm="8"
                    >
                      <v-switch
                        v-if="inventoryCount.type === INVENTORY_COUNT_FULL"
                        v-model="inventoryCount.excludeOutOfStock"
                        :label="$t('Exclude products that are out of stock')"
                        class="mt-4"
                        color="primary"
                        hide-details
                        inset
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-form>
          </form-wrapper>
          <v-divider class="mt-4" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            :loading="$wait.is('saving inventorycount')"
            class="font-weight-bold"
            color="primary"
            text
            @click="submit"
          >{{ $t('Start') }}</v-btn>
        </v-card-actions>
      </v-container>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import snakecaseKeys from 'snakecase-keys'
import validationMixin from '@/utils/mixins/validationMixin'
import focusInputMixin from '@/utils/mixins/focusInputMixin'
import inventoryCountService from '@/services/inventory-counts'
import { INVENTORY_COUNT_PARTIAL, INVENTORY_COUNT_FULL, INVENTORY_COUNT_INCOMING } from '@/utils/types/inventory-count'

export default {
  name: 'FormsInventoryCountsAdd',

  mixins: [ validationMixin, focusInputMixin ],

  data: () => ({
    INVENTORY_COUNT_PARTIAL,
    INVENTORY_COUNT_FULL,
    INVENTORY_COUNT_INCOMING,
    inventoryCount: {
      type: INVENTORY_COUNT_PARTIAL,
      excludeOutOfStock: false
    }
  }),

  computed: {
    ...mapGetters(['cashRegister'])
  },

  watch: {
    // reset the excludeOutOfStock value on type change
    'inventoryCount.type': function (value) {
      this.$set(this.inventoryCount, 'excludeOutOfStock', false)
    }
  },

  validations () {
    const validations = {
      inventoryCount: {
        name: { required },
        type: { required }
      }
    }

    return validations
  },

  methods: {
    submit () {
      if (!this.validate()) return
      this.$wait.start('saving inventorycount')

      return inventoryCountService.add(snakecaseKeys(this.inventoryCount)).then((data) => {
        this.helpers.toastSuccess(data.data.message)
        this.$emit('saved', data.data.inventory_count)
      })
        .finally((data) => this.$wait.end('saving inventorycount'))
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/forms/inventory-counts/FormsInventoryCountsAdd.vue